/**
 * P2-B — Book-a-Consultation modal (Klaviyo).
 *
 * Upgrades the "Book a showroom visit" mailto link (data-csc-consult-open) on
 * custom PDPs into an accessible modal dialog that submits a Klaviyo Client
 * Event ("Requested Consultation") via the public, CORS-enabled Client API.
 * No external libraries. With JS off, the link's mailto: still works.
 *
 * a11y: role="dialog" aria-modal, focus moves in on open, focus trap, ESC +
 * overlay-click close, focus restored to the trigger on close.
 */
(function () {
  if (window.__cscConsultInit) return;        // guard: only wire once per page
  window.__cscConsultInit = true;

  var COMPANY_ID = 'QSKrPU';
  var EVENTS_URL = 'https://a.klaviyo.com/client/events/?company_id=' + COMPANY_ID;
  var SUBS_URL   = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + COMPANY_ID;
  var REVISION   = '2024-10-15';
  var MARKETING_LIST = 'W5W5AL';              // double-opt-in list (optional consent)
  var SALES_EMAIL = 'xavier.labelle@canadianspacompany.com';

  function init() {
    var modal = document.querySelector('[data-csc-consult-modal]');
    if (!modal) return;                        // page has no consultation row

    var dialog   = modal.querySelector('.csc-consult-modal__dialog');
    var overlay  = modal.querySelector('[data-csc-consult-overlay]');
    var form     = modal.querySelector('[data-csc-consult-form]');
    var statusEl = modal.querySelector('[data-csc-consult-status]');
    var submitEl = modal.querySelector('[data-csc-consult-submit]');
    var titleField = modal.querySelector('[data-csc-consult-field-title]');
    var urlField   = modal.querySelector('[data-csc-consult-field-url]');
    var productEls = modal.querySelectorAll('[data-csc-consult-product]');
    var lastFocused = null;

    // ── open / close ──────────────────────────────────────────────────
    function openModal(trigger) {
      lastFocused = trigger || document.activeElement;
      var title = trigger && trigger.getAttribute('data-product-title');
      var url   = trigger && trigger.getAttribute('data-product-url');
      if (title) {
        if (titleField) titleField.value = title;
        for (var i = 0; i < productEls.length; i++) productEls[i].textContent = title;
      }
      if (url && urlField) urlField.value = url;

      modal.hidden = false;
      document.body.classList.add('csc-consult-modal-open');
      // focus the first form FIELD (Name) so the user can start typing immediately
      var first = form.querySelector('input:not([type="hidden"]):not([tabindex="-1"]), textarea, select') || getFocusable()[0];
      if (first) { try { first.focus(); } catch (e) {} }
    }

    function closeModal() {
      modal.hidden = true;
      document.body.classList.remove('csc-consult-modal-open');
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus(); } catch (e) {}
      }
    }

    function getFocusable() {
      return Array.prototype.filter.call(
        dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]'),
        function (el) {
          return !el.disabled && el.tabIndex !== -1 && el.type !== 'hidden' && el.offsetParent !== null;
        }
      );
    }

    // ── triggers ──────────────────────────────────────────────────────
    document.querySelectorAll('[data-csc-consult-open]').forEach(function (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        openModal(trigger);
      });
    });

    if (overlay) overlay.addEventListener('click', closeModal);
    modal.querySelectorAll('[data-csc-consult-close]').forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', function (e) {
      if (modal.hidden) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        closeModal();
        return;
      }
      if (e.key === 'Tab') {
        var f = getFocusable();
        if (!f.length) return;
        var firstEl = f[0], lastEl = f[f.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault(); lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault(); firstEl.focus();
        } else if (!dialog.contains(document.activeElement)) {
          e.preventDefault(); firstEl.focus();
        }
      }
    });

    // ── helpers ───────────────────────────────────────────────────────
    function val(name) {
      var el = form.elements[name];
      return el ? String(el.value || '').trim() : '';
    }

    function isEmail(s) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s); }

    // UK best-effort E.164 — raw value is ALSO sent so nothing is lost.
    function toE164(raw) {
      if (!raw) return '';
      var s = String(raw).replace(/[^\d+]/g, '');
      if (!s) return '';
      if (s.charAt(0) === '+') return s;
      if (s.indexOf('44') === 0) return '+' + s;
      if (s.charAt(0) === '0') return '+44' + s.slice(1);
      return '+' + s;
    }

    function setStatus(msg, kind) {
      if (!statusEl) return;
      statusEl.className = 'csc-consult-modal__status' + (kind ? ' csc-consult-modal__status--' + kind : '');
      statusEl.textContent = msg || '';
    }

    function showSuccess() {
      // Replace the form body with a thanks message; keep modal open + close btn usable.
      form.innerHTML =
        '<div class="csc-consult-modal__success" role="status" aria-live="polite">' +
        '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>' +
        '<p>Thanks — we’ll be in touch to confirm your visit.</p>' +
        '</div>';
    }

    function showError() {
      setStatus('', null);
      statusEl.className = 'csc-consult-modal__status csc-consult-modal__status--error';
      statusEl.innerHTML = 'Something went wrong — email us directly at ' +
        '<a href="mailto:' + SALES_EMAIL + '">' + SALES_EMAIL + '</a>.';
    }

    function buildEventPayload(d) {
      return {
        data: {
          type: 'event',
          attributes: {
            metric: { data: { type: 'metric', attributes: { name: 'Requested Consultation' } } },
            properties: {
              'Product': d.productTitle,
              'Product URL': d.productUrl,
              'Preferred Showroom Dates': d.dates,
              'Phone (raw)': d.phoneRaw,
              'Source': 'PDP consultation modal'
            },
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: d.email,
                  phone_number: d.phoneE164,
                  first_name: d.name,
                  properties: { 'Last Consultation Product': d.productTitle }
                }
              }
            }
          }
        }
      };
    }

    // MARKETING (optional) — delete this function + its call to remove the opt-in.
    function subscribeMarketing(email) {
      if (!email) return;
      fetch(SUBS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': REVISION },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'PDP consultation modal',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: MARKETING_LIST } } }
          }
        })
      }).catch(function () { /* non-blocking: the event is the primary mechanism */ });
    }

    // ── submit ────────────────────────────────────────────────────────
    form.addEventListener('submit', function (e) {
      e.preventDefault();

      // 1. Honeypot — if filled, silently pretend success and send nothing.
      if (val('company')) { showSuccess(); return; }

      // 2. Validate (form is novalidate; we control the messaging).
      var name = val('name'), email = val('email'), phoneRaw = val('phone'), dates = val('dates');
      if (!name || !email || !phoneRaw || !dates) {
        setStatus('Please fill in your name, email, phone and preferred dates.', 'error');
        return;
      }
      if (!isEmail(email)) {
        setStatus('Please enter a valid email address.', 'error');
        var em = form.elements['email']; if (em) em.focus();
        return;
      }

      var data = {
        name: name,
        email: email,
        phoneRaw: phoneRaw,
        phoneE164: toE164(phoneRaw),
        dates: dates,
        productTitle: (titleField && titleField.value) || '',
        productUrl: (urlField && urlField.value) || ''
      };

      submitEl.disabled = true;
      submitEl.textContent = 'Sending…';
      setStatus('', null);

      var marketingEl = form.querySelector('[data-csc-consult-marketing]');
      var wantsMarketing = !!(marketingEl && marketingEl.checked);

      fetch(EVENTS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': REVISION },
        body: JSON.stringify(buildEventPayload(data))
      })
        .then(function (res) {
          if (res.status === 202 || res.ok) {
            if (wantsMarketing) subscribeMarketing(email);   // optional, non-blocking
            showSuccess();
          } else {
            throw new Error('HTTP ' + res.status);
          }
        })
        .catch(function () {
          submitEl.disabled = false;
          submitEl.textContent = 'Request my visit';
          showError();
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
